import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiFormError, ENVIRONMENT, IEnvironment } from '@trade-platform/ui-utils';
import { from as fromPromise, Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { orderFiltersDecoder, orderIdDecoder } from '../decoder';
import { DocumentFile, OrderId, OrdersStatusHistory } from '../model';
import { maintenanceOrderDecoder } from './decoder';
import { SignaturesPreviewResponse } from '../../order-form/model';
import { MaintenanceOrder } from './model';

@Injectable()
export class MaintenanceOrderService {
    private http = inject(HttpClient);
    private environment = inject<IEnvironment>(ENVIRONMENT);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    getMaintenanceOrder(id: string): Observable<MaintenanceOrder> {
        return this.http
            .get<MaintenanceOrder>(`${this.environment.api}maintenance/${id}`)
            .pipe(concatMap(p => fromPromise(maintenanceOrderDecoder.decodeToPromise(p))));
    }

    pollMaintenanceOrder(id: string): Observable<MaintenanceOrder> {
        return this.getMaintenanceOrder(id);
    }

    getMaintenanceOrdersStatusHistory(orderId: string): Observable<OrdersStatusHistory[]> {
        return this.http
            .get<OrdersStatusHistory[]>(
                `${this.environment.api}maintenance/${orderId}/statusHistory`
            )
            .pipe(concatMap(p => fromPromise(orderFiltersDecoder.decodeToPromise(p))));
    }

    updateMaintenanceOrder(
        maintenanceOrder: MaintenanceOrder,
        formCompleted = false
    ): Observable<OrderId> {
        const payload: any = { ...maintenanceOrder };
        delete payload.id;
        const http = this.http.patch<OrderId>(
            `${this.environment.api}maintenance/${maintenanceOrder.id}?formCompleted=${formCompleted}`,
            payload
        );

        http.pipe(
            concatMap(o => fromPromise(orderIdDecoder.decodeToPromise(o))),
            map(o => {
                if (o.validation && !o.validation.orderPassed) {
                    // Force error because server validation errors are 200 OK
                    throw new ApiFormError('', o.validation.failures, '');
                }
                return o;
            })
        );

        return http;
    }

    updateMaintenanceOrderFiles(files: DocumentFile[], orderId: string): Observable<OrderId> {
        const filesToUpdate = files.map(file => {
            if (file.formId) {
                return {
                    id: file.id,
                    formId: file.formId,
                    name: file.name,
                    firmId: file.firmId,
                    fundId: file.fundId,
                    holdingOptionId: file.holdingOptionId
                };
            }
            return {
                id: file.id,
                contextMap: file.contextMap,
                isPrivate: file.isPrivate,
                supplementalFileTypeId: file.supplementalFileTypeId
            };
        });
        const http = this.http.put<OrderId>(
            `${this.environment.api}maintenance/${orderId}/files`,
            filesToUpdate
        );

        http.pipe(
            concatMap(o => fromPromise(orderIdDecoder.decodeToPromise(o))),
            map(o => {
                if (o.validation && !o.validation.orderPassed) {
                    // Force error because server validation errors are 200 OK
                    throw new ApiFormError('', o.validation.failures, '');
                }
                return o;
            })
        );

        return http;
    }

    getSignaturesPreview(orderId: string): Observable<SignaturesPreviewResponse> {
        return this.http.get<SignaturesPreviewResponse>(
            `${this.environment.api}maintenance/${orderId}/getSignaturesPreview`
        );
    }

    /**
     * Renders one form as a filled PDF.
     *
     * POST rather than GET because the draft travels in the body: the server renders from the
     * order it has stored, so anything not saved yet -- including the stamping map of a
     * pre-populated form nobody has typed into -- has to be sent with the request. Nothing sent
     * here is persisted, and no cache buster is needed because POST responses are not cached.
     *
     * There is deliberately no GET fallback for an advisors-api that predates the draft overlay.
     * It would render the saved order while the overlay went on telling the user it was showing
     * the answers currently on the form -- a downloadable document contradicting its own notice,
     * and precisely in the window the fallback existed for. A stale pod is a visible error
     * instead. The rollout already requires advisors-api to deploy first.
     */
    getFormPreview(orderId: string, formId: string, draft?: unknown): Observable<Blob> {
        return this.http.post(
            `${this.environment.api}maintenance/${orderId}/forms/${formId}/preview`,
            draft ?? {},
            { responseType: 'blob' }
        );
    }
}
